import { useCallback, useState } from 'react';
import { faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { selectAdapters } from 'ducks/adapters/selectors';
import { updateModal } from 'ducks/modals/slices';
import { updateOverlay } from 'ducks/overlays/slices';
import { selectPairs } from 'ducks/pairs/selectors';
import { selectStrategyStatus } from 'ducks/statuses/selectors';
import { selectStrategies } from 'ducks/strategies/selectors';
import { Button } from 'framework/button';
import { Grid } from 'framework/Grid';
import { GridFieldCell } from 'framework/Grid/cells/GridCell';
import { GridColumn } from 'framework/Grid/columns/GridColumn';
import { Input } from 'framework/Input';
import { useStrategyApi } from 'pages/hooks/useStrategyApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { filterGrid } from 'utils/strings';

import { GridPairStrategyCell } from 'components/cells/GridPairStrategyCell';
import { GridStatusChipCell } from 'components/cells/GridStatusChipCell';
import { GridStrategyEllipsisCell } from 'components/cells/GridStrategyEllipsisCell';
import { GridAutoSizer } from 'components/grid/GridScrollable';
import { AccordionCheckboxes } from 'components/overlays/accordions/accordion-checkboxes/AccordionCheckboxes';
import { Filters } from 'components/overlays/filters/Filters';
import { StrategiesOverlay } from 'components/overlays/strategies/StrategiesOverlay';

import cn from '../StrategiesPage.module.scss';

export const StrategiesGrid = () => {
  const dispatch = useAppDispatch();
  const voladeum_pairs = useAppSelector(selectPairs);
  const voladeum_adapters = useAppSelector(selectAdapters);
  const voladeum_strategies = useAppSelector(selectStrategies);
  const voladeum_strategy_status = useAppSelector(selectStrategyStatus);

  const { getStrategyLookups, getStrategyFilters } = useStrategyApi();
  const [filter, setFilter] = useState('');

  const onClick = useCallback(async () => {
    await getStrategyFilters();
    dispatch(updateOverlay({ key: 'strategies', data: { is_open: true } }));
  }, [getStrategyFilters, dispatch]);

  const onCreate = useCallback(async () => {
    await getStrategyLookups();
    dispatch(updateModal({ key: 'strategy_manage', data: { is_open: true } }));
  }, [dispatch, getStrategyLookups]);
  return (
    <div className={cn.grid}>
      <div className={cn.toolbar}>
        <h2>Strategies</h2>
        <div className={cn.flex} />
        <Input
          left_icon={faSearch}
          value={filter}
          onChange={(input) => setFilter(input.value)}
          width="300px"
        />
        <Filters onClick={onClick}>
          <StrategiesOverlay>
            <AccordionCheckboxes
              label="Adapters"
              options={voladeum_adapters}
              filter_key="strategy_adapter_ids"
              value_key="adapter_name"
              id_key="adapter_id"
            />
            <AccordionCheckboxes
              label="Pairs"
              options={voladeum_pairs}
              filter_key="strategy_pair_ids"
              value_key="pair_name"
              id_key="pair_id"
            />
            <AccordionCheckboxes
              label="Status"
              options={voladeum_strategy_status}
              filter_key="strategy_status_ids"
              value_key="status_name"
              id_key="status_id"
            />
          </StrategiesOverlay>
        </Filters>
        <Button left_icon={faPlus} onClick={onCreate}>
          Create Strategy
        </Button>
      </div>
      <GridAutoSizer>
        <Grid rows={filterGrid(voladeum_strategies, filter)}>
          <GridColumn
            label="Pair"
            sort_field="pair_name"
            sort_direction="desc"
            width={300}
            text_align="left"
          >
            <GridPairStrategyCell />
          </GridColumn>
          <GridColumn label="Status" sort_field="status_name" width={150}>
            <GridStatusChipCell />
          </GridColumn>
          <GridColumn label="Adapter" sort_field="adapter_name" width={100} grow={1}>
            <GridFieldCell field="adapter_name" />
          </GridColumn>
          <GridColumn label="Sizing Method" sort_field="sizing_method_name" width={125} grow={1}>
            <GridFieldCell field="sizing_method_name" />
          </GridColumn>
          <GridColumn label="Max Position" sort_field="max_position" width={125} grow={1}>
            <GridFieldCell field="max_position_formatted" />
          </GridColumn>
          <GridColumn label="Max Daily Loss" sort_field="max_daily_loss" width={125} grow={1}>
            <GridFieldCell field="max_daily_loss_formatted" />
          </GridColumn>
          <GridColumn width={40} fixed="right">
            <GridStrategyEllipsisCell />
          </GridColumn>
        </Grid>
      </GridAutoSizer>
    </div>
  );
};
